import type { ToolCallStatus } from './const';
import type { ToolCallEvent } from './StreamParser/types';
import { AppError, ErrorType } from '@/helpers/error';
import { OpenAIParser } from './StreamParser/OpenAIParser';

/**
 * 待规范化的原始工具调用
 */
export interface RawToolCallLike {
  id?: string;
  toolCallId?: string;
  name?: string;
  toolName?: string;
  arguments?: unknown;
}

/** 规范化后的工具审批条目 */
export interface NormalizedApprovalToolCall {
  toolCallId: string;
  toolName: string;
  arguments?: string;
  toUser?: string;
}

/**
 * 将历史/流式下发的原始 toolCall 统一规范化为审批条目，
 * 并尝试从 arguments 中提取 to_user 作为友好展示。
 */
export function normalizeToolCallForApproval(tc: RawToolCallLike): NormalizedApprovalToolCall {
  let toUser: string | undefined;
  let argsString: string | undefined;

  if (tc.arguments !== undefined && tc.arguments !== null) {
    if (typeof tc.arguments === 'string') {
      argsString = tc.arguments;
      try {
        const parsed = JSON.parse(tc.arguments);
        toUser = (parsed as { to_user?: string })?.to_user;
      } catch { /* ignore */ }
    } else {
      if (typeof tc.arguments === 'object') {
        toUser = (tc.arguments as { to_user?: string })?.to_user;
      }
      try {
        argsString = JSON.stringify(tc.arguments);
      } catch { /* ignore */ }
    }
  }

  return {
    toolCallId: tc.toolCallId || tc.id || '',
    toolName: tc.toolName || tc.name || '',
    arguments: argsString,
    toUser,
  };
}

/**
 * 将 API 返回的状态映射到标准状态
 */
export function mapToolCallStatus(status: string | undefined): ToolCallStatus {
  if (!status) {
    return 'in_progress';
  }

  switch (status) {
    case 'calling':
    case 'in_progress':
      return 'in_progress';
    case 'failed':
    case 'incomplete':
      return 'incomplete';
    case 'completed':
    default:
      return 'completed';
  }
}

/**
 * SSE 事件外层包装接口
 */
export interface OutputDoneEvent {
  finishReason?: string;
  continueAttempt?: number;
}

export interface ResponseResetEvent {
  name: 'response_reset';
  messageId: string;
  sessionId?: string;
  reason: 'content_filter';
  timestamp: number;
}

export interface SSEEnvelope {
  id: number;
  event: 'message_id' | 'reply_language' | 'agent_event' | 'reference_image_ready' | 'image_generated' | 'response_reset' | 'output_done' | 'error' | 'continue_generating' | 'content_filter' | 'tool_approval_required' | 'tool_approval_auto_approved' | 'interrupted' | 'user_input_suspended';
  data: any;
}

export function isValidResponseResetEvent(
  event: unknown,
  resetEventId: number,
): event is ResponseResetEvent {
  if (!event || typeof event !== 'object') {
    return false;
  }

  const candidate = event as Partial<ResponseResetEvent>;
  const isValidEventId = (value: unknown): value is number =>
    typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

  return isValidEventId(resetEventId)
    && candidate.name === 'response_reset'
    && typeof candidate.messageId === 'string'
    && candidate.messageId.length > 0
    && candidate.reason === 'content_filter'
    && typeof candidate.timestamp === 'number'
    && Number.isFinite(candidate.timestamp);
}

/**
 * 解析 SSE 原始文本
 *
 * 格式:
 * ```
 * id: 1
 * event: agent_event
 * data: {...}
 * ```
 *
 * @param text SSE 事件文本块
 * @returns 解析后的事件对象，解析失败返回 null
 */
export function parseSSEEnvelope(text: string): SSEEnvelope | null {
  const lines = text.trim().split('\n');
  const envelope: Partial<SSEEnvelope> = {};

  for (const line of lines) {
    if (line.startsWith('id: ')) {
      envelope.id = Number.parseInt(line.substring(4), 10);
    } else if (line.startsWith('event: ')) {
      envelope.event = line.substring(7) as any;
    } else if (line.startsWith('data: ')) {
      try {
        envelope.data = JSON.parse(line.substring(6));
      } catch (e) {
        console.error('[SSEParser] 解析 data 字段失败:', e);
        return null;
      }
    }
  }

  // 验证必需字段
  if (!envelope.event || !envelope.data || typeof envelope.id !== 'number') {
    return null;
  }

  return envelope as SSEEnvelope;
}

/**
 * 后端 tool_approval_required 事件中的审批项
 */
export interface SSEApprovalItem {
  id: string;
  toolName: string;
  arguments?: unknown;
}

/**
 * 后端 tool_approval_required 事件数据
 */
export interface ToolApprovalRequiredEvent {
  messageId: string;
  sessionId?: string;
  approvals: SSEApprovalItem[];
  timestamp: number;
  autoApproveAt: number;
}

export interface ToolApprovalAutoApprovedEvent {
  name: 'tool_approval_auto_approved';
  messageId: string;
  sessionId?: string;
  toolCallIds: string[];
  timestamp: number;
}

export function isValidToolApprovalRequiredEvent(event: unknown): event is ToolApprovalRequiredEvent {
  if (!event || typeof event !== 'object') {
    return false;
  }

  const candidate = event as Partial<ToolApprovalRequiredEvent>;
  return typeof candidate.messageId === 'string'
    && candidate.messageId.length > 0
    && (candidate.sessionId === undefined || typeof candidate.sessionId === 'string')
    && Array.isArray(candidate.approvals)
    && candidate.approvals.length > 0
    && candidate.approvals.every(approval => Boolean(approval)
      && typeof approval.id === 'string'
      && approval.id.length > 0
      && typeof approval.toolName === 'string'
      && approval.toolName.length > 0)
    && typeof candidate.timestamp === 'number'
    && Number.isFinite(candidate.timestamp)
    && typeof candidate.autoApproveAt === 'number'
    && Number.isFinite(candidate.autoApproveAt);
}

export function isValidToolApprovalAutoApprovedEvent(event: unknown): event is ToolApprovalAutoApprovedEvent {
  if (!event || typeof event !== 'object') {
    return false;
  }

  const candidate = event as Partial<ToolApprovalAutoApprovedEvent>;
  return candidate.name === 'tool_approval_auto_approved'
    && typeof candidate.messageId === 'string'
    && candidate.messageId.length > 0
    && (candidate.sessionId === undefined || typeof candidate.sessionId === 'string')
    && Array.isArray(candidate.toolCallIds)
    && candidate.toolCallIds.every(toolCallId => typeof toolCallId === 'string' && toolCallId.length > 0)
    && typeof candidate.timestamp === 'number'
    && Number.isFinite(candidate.timestamp);
}

export interface UserInputSuspendedEvent {
  messageId: string;
  interruptId: string;
  sessionId: string;
  type: string;
  timestamp?: number;
}

/**
 * processSSEStream 回调接口
 */
export interface ProcessSSECallbacks {
  onChunk: (chunk: string) => void;
  onComplete: (event?: OutputDoneEvent) => void;
  onError: (error: Error) => void;
  onConversationStart?: (messageId: string) => void;
  onReplyLanguage?: (language: string) => void | Promise<void>;
  onToolCall?: (event: ToolCallEvent) => void;
  onResponseStarted?: () => void;
  onResponseDone?: (data: { text: string; responseId?: string }) => void;
  onThinkingDelta?: (delta: string) => void;
  onThinkingDone?: () => void;
  onResponseReset?: (event: ResponseResetEvent) => void;
  onToolApprovalRequired?: (event: ToolApprovalRequiredEvent) => void;
  onToolApprovalAutoApproved?: (event: ToolApprovalAutoApprovedEvent) => void;
  onInterrupted?: (data: { messageId: string; reason: string }) => void;
  onUserInputSuspended?: (data: UserInputSuspendedEvent) => void;
}

/**
 * 通用 SSE 流处理函数
 * 处理 ReadableStream 并将 SSE 事件分发到相应的回调
 */
export async function processSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  callbacks: ProcessSSECallbacks,
): Promise<void> {
  const {
    onChunk,
    onComplete,
    onError,
    onConversationStart,
    onReplyLanguage,
    onToolCall,
    onResponseStarted,
    onResponseDone,
onThinkingDelta,
    onThinkingDone,
    onResponseReset,
    onToolApprovalRequired,
    onToolApprovalAutoApproved,
    onUserInputSuspended,
  } = callbacks;
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  // 创建 SSE 解析器
  const parser = new OpenAIParser(false);

  while (true) {
    const { done, value } = await reader.read();

    if (done) {
      // 没有 output_done 时走错误收敛
      onError(new AppError(
        ErrorType.SSE_ERROR_EVENT,
        'SSE stream ended before a terminal event was received',
      ));
      return;
    }

    buffer += decoder.decode(value, { stream: true });

    // SSE 格式使用双换行符分隔事件
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';

    for (const eventText of events) {
      if (!eventText.trim()) {
        continue;
      }

      const envelope = parseSSEEnvelope(eventText);
      if (!envelope) {
        continue;
      }

      // 处理不同事件类型
      switch (envelope.event) {
        case 'message_id':
          // 整轮对话开始（只会触发一次）
          if (envelope.data?.messageId && onConversationStart) {
            onConversationStart(envelope.data.messageId);
          }
          break;

        case 'reply_language':
          if (typeof envelope.data?.language === 'string') {
            await onReplyLanguage?.(envelope.data.language);
          }
          break;

        case 'agent_event': {
          // 主要事件：将 data 传给 OpenAIParser
          const innerEvent = parser.parseLine(JSON.stringify(envelope.data));
          if (!innerEvent) {
            break;
          }

          // 分发事件
          switch (innerEvent.type) {
            case 'response_started':
              // 新响应不复用上一响应的 thinking、Tool 和文本去重状态
              parser.reset();
              onResponseStarted?.();
              break;

            case 'response_done':
              onResponseDone?.(innerEvent.data);
              break;

            case 'text_delta':
              onChunk(innerEvent.data.delta);
              break;

            case 'thinking_delta':
              onThinkingDelta?.(innerEvent.data.delta);
              break;

            case 'thinking_done':
              onThinkingDone?.();
              break;

            case 'error': {
              const errorMessage = innerEvent.data.error && typeof innerEvent.data.error === 'string'
                ? innerEvent.data.error
                : undefined;
              onError(new AppError(ErrorType.SSE_AGENT_EVENT, errorMessage));
              return;
            }

            case 'tool_call_start':
              onToolCall?.({
                toolId: innerEvent.data.toolId,
                requestId: innerEvent.data.requestId,
                callId: innerEvent.data.callId,
                toolName: innerEvent.data.toolName,
                status: 'calling',
              });
              break;

            case 'tool_call_delta':
              onToolCall?.({
                toolId: innerEvent.data.toolId,
                requestId: innerEvent.data.requestId,
                callId: innerEvent.data.callId,
                toolName: innerEvent.data.toolName,
                status: 'calling',
                arguments: innerEvent.data.partialArguments,
              });
              break;

            case 'tool_call_complete':
              onToolCall?.({
                toolId: innerEvent.data.toolId,
                requestId: innerEvent.data.requestId,
                callId: innerEvent.data.callId,
                toolName: innerEvent.data.toolName,
                status: 'calling',
                arguments: innerEvent.data.arguments,
              });
              break;

            case 'tool_called':
              // 工具调用开始，先结束 thinking
              onThinkingDone?.();
              onToolCall?.({
                toolId: innerEvent.data.toolId,
                requestId: innerEvent.data.requestId,
                callId: innerEvent.data.callId,
                toolName: innerEvent.data.toolName,
                status: 'calling',
                arguments: innerEvent.data.arguments,
              });
              break;

            case 'tool_output':
              onToolCall?.({
                toolId: innerEvent.data.toolId,
                requestId: innerEvent.data.requestId,
                callId: innerEvent.data.callId,
                toolName: innerEvent.data.toolName,
                status: innerEvent.data.status,
                output: innerEvent.data.output,
              });
              break;
          }
          break;
        }

        case 'reference_image_ready':
          if (envelope.data?.url) {
            onToolCall?.({
              toolId: '',
              toolName: 'gen_reference_ui',
              status: 'calling',
              progressData: envelope.data,
            });
          }
          break;

        case 'image_generated':
          if (envelope.data?.url) {
            onToolCall?.({
              toolId: '',
              toolName: 'gen_image',
              status: 'calling',
              progressData: envelope.data,
            });
          }
          break;

        case 'response_reset':
          if (isValidResponseResetEvent(envelope.data, envelope.id)) {
            onResponseReset?.(envelope.data);
          } else {
            console.warn('[SSEParser] 忽略非法 response_reset 事件', {
              eventId: envelope.id,
              data: envelope.data,
            });
          }
          break;

        case 'output_done':
          // 完成事件
          onComplete(envelope.data);
          return;

        case 'error': {
          const errorMessage =
            (envelope.data.error && envelope.data.error.trim()) ||
            (envelope.data.message && envelope.data.message.trim()) ||
            'Agent output error';
          onError(new AppError(ErrorType.SSE_ERROR_EVENT, errorMessage));
          return;
        }

        case 'content_filter': {
          const errorMessage = typeof envelope.data.message === 'string' && envelope.data.message.trim()
            ? envelope.data.message
            : 'AI output has been filtered by content policy.';
          onError(new AppError(ErrorType.CONTENT_FILTER, errorMessage));
          return;
        }

        case 'tool_approval_required':
          if (isValidToolApprovalRequiredEvent(envelope.data)) {
            onToolApprovalRequired?.(envelope.data);
          } else {
            console.warn('[SSEParser] 忽略非法 tool_approval_required 事件', {
              eventId: envelope.id,
              messageId: envelope.data?.messageId,
            });
          }
          break;

        case 'tool_approval_auto_approved':
          if (isValidToolApprovalAutoApprovedEvent(envelope.data)) {
            onToolApprovalAutoApproved?.(envelope.data);
          } else {
            console.warn('[SSEParser] 忽略非法 tool_approval_auto_approved 事件', {
              eventId: envelope.id,
              messageId: envelope.data?.messageId,
            });
          }
          break;

        case 'interrupted':
          if (callbacks.onInterrupted) {
            callbacks.onInterrupted(envelope.data);
          }
          return;

        case 'user_input_suspended':
          onUserInputSuspended?.(envelope.data);
          await reader.cancel();
          return;

        case 'continue_generating':
          // 自动续写事件，暂不处理
          break;
      }
    }
  }
}
